import { Component, createRef, RefObject } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './palette-extractor.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; quantize: any }
    | { kind: 'error'; message: string };

interface State {
    fileName: string;
    imageUrl: string | null;
    paletteSize: number;
    palette: { hex: string; rgb: [number, number, number]; weight: number }[];
    busy: boolean;
    lib: LibState;
    copiedIdx: number | null;
}

function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map(x => Math.round(x).toString(16).padStart(2, '0')).join('');
}

export default class PaletteExtractorComponent extends Component<{ feature?: string }, State> {
    private canvasRef: RefObject<HTMLCanvasElement | null> = createRef();
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        fileName: '',
        imageUrl: null,
        paletteSize: 6,
        palette: [],
        busy: false,
        lib: { kind: 'idle' },
        copiedIdx: null,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async ensureLib(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.quantize;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const mod = await import('quantize');
            const quantize = (mod as any).default || mod;
            this.setState({ lib: { kind: 'ready', quantize } });
            return quantize;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load quantize' } });
            return null;
        }
    }

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const url = URL.createObjectURL(f);
        this.setState({ imageUrl: url, fileName: f.name, palette: [] });
    };

    private async loadImageToPixels(): Promise<[number, number, number][] | null> {
        if (!this.state.imageUrl || !this.canvasRef.current) return null;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        return new Promise((resolve) => {
            img.onload = () => {
                const canvas = this.canvasRef.current!;
                const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
                const maxDim = 300;
                const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
                canvas.width = Math.floor(img.width * scale);
                canvas.height = Math.floor(img.height * scale);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
                const pixels: [number, number, number][] = [];
                for (let i = 0; i < data.length; i += 4) {
                    if (data[i + 3] >= 128) pixels.push([data[i], data[i + 1], data[i + 2]]);
                }
                resolve(pixels);
            };
            img.onerror = () => resolve(null);
            img.src = this.state.imageUrl!;
        });
    }

    extract = async () => {
        this.setState({ busy: true });
        const quantize = await this.ensureLib();
        if (!quantize) { this.setState({ busy: false }); return; }
        const pixels = await this.loadImageToPixels();
        if (!pixels) { this.setState({ busy: false }); return; }
        const cmap = quantize(pixels, this.state.paletteSize);
        if (!cmap) { this.setState({ busy: false }); return; }
        const palette = cmap.palette().map((rgb: [number, number, number]) => ({
            hex: rgbToHex(rgb[0], rgb[1], rgb[2]),
            rgb,
            weight: 1,
        }));
        this.setState({ palette, busy: false });
    };

    copyHex = (hex: string, i: number) => {
        navigator.clipboard.writeText(hex).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedIdx: i });
            this.copyTimeout = setTimeout(() => this.setState({ copiedIdx: null }), 1500);
        });
    };

    render() {
        const { fileName, imageUrl, paletteSize, palette, busy, lib, copiedIdx } = this.state;
        return (
            <div className="row">
                <div className="col-12 col-md-5">
                    <div className={genStyles.section}>
                        {/* No absolutes in user-facing copy (owner decision 2026-09-01, task B5) —
                            state where processing happens, never "never uploaded" / "100% private". */}
                        <label className={sharedStyles.label}>Image (processed locally in your browser)</label>
                        <div className={styles.fileBox}>
                            <input type="file" id="palette-file" accept="image/*" className={styles.fileInput}
                                onChange={this.onFileChange} />
                            <label htmlFor="palette-file" className={sharedStyles.buttonPrimary}>Choose image</label>
                            {fileName && <div className={styles.fileName}>{fileName}</div>}
                        </div>
                        <label className={sharedStyles.label}>
                            Palette size
                            <input className={styles.input} type="number" inputMode="decimal" min={2} max={20} value={paletteSize}
                                onChange={e => this.setState({ paletteSize: Math.max(2, Math.min(20, +e.target.value || 6)) })} />
                        </label>
                        <button className={sharedStyles.buttonPrimary} onClick={this.extract}
                            disabled={!imageUrl || busy || lib.kind === 'loading'}>
                            {busy ? 'Extracting…' : lib.kind === 'loading' ? 'Loading quantize…' : 'Extract palette'}
                        </button>
                        {lib.kind === 'error' && <p className={styles.error} role="alert">{lib.message}</p>}
                    </div>
                </div>
                <div className="col-12 col-md-7">
                    <div className={genStyles.section}>
                        {imageUrl && (
                            <>
                                <label className={sharedStyles.label}>Image</label>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img src={imageUrl} alt="Source" className={styles.preview} />
                            </>
                        )}
                        {palette.length > 0 && (
                            <>
                                <label className={sharedStyles.label} style={{ marginTop: '1rem' }}>Extracted palette</label>
                                <div className={styles.paletteGrid}>
                                    {palette.map((c, i) => (
                                        <button key={i} className={styles.swatch} style={{ background: c.hex }}
                                            onClick={() => this.copyHex(c.hex, i)}>
                                            <span className={styles.swatchHex}>
                                                {copiedIdx === i ? '✓ Copied' : c.hex}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                        <canvas ref={this.canvasRef} style={{ display: 'none' }} />
                    </div>
                </div>
            </div>
        );
    }
}
